import { useEffect, useRef, useState } from "react";

import {
  armarRegla,
  armarValidacion,
  describirRegla,
  ESTILO_INICIAL,
  FONDOS_REGLA,
  parseRango,
  reglasEn,
  TEXTOS_REGLA,
  textoRango,
  TIPOS,
} from "../shared/formatoCondicional";

import "./css/condicional.css";

/* Formato condicional y listas desplegables: lo que se ve de FiboSheets.
 *
 * La lógica (cuándo se cumple una regla, cómo se corren los rangos) está en
 * `shared/formatoCondicional`. Acá:
 *
 *   · BotonesReglas: los dos botones de la barra de herramientas.
 *   · PanelReglas: el panel que se abre abajo de la barra para ver, agregar y
 *     borrar las reglas del rango seleccionado (o sus listas desplegables).
 *   · MenuLista: la lista que aparece al tocar la flechita de una celda.
 *
 * Todo se maneja con el dedo: botones de 40 px de alto en el celular, nada
 * que dependa del hover, y la lista se cierra tocando afuera. */

const TIPO_POR_DEFECTO = "mayor";

/* Los dos botones de la barra. `abierto` es cuál panel está a la vista. */
export function BotonesReglas({ hay, abierto, onAbrir }) {
  const alternar = (cual) => onAbrir(abierto === cual ? null : cual);
  return (
    <>
      <span className="esp-sep" />
      <button
        className={"esp-btn chico" + (abierto === "condicional" ? " sel" : "")} disabled={!hay}
        aria-pressed={abierto === "condicional"} onClick={() => alternar("condicional")}
        title="Pintar las celdas que cumplen una condición (p. ej. lo que vence en 15 días)"
      >🎨 Formato condicional</button>
      <button
        className={"esp-btn chico" + (abierto === "validacion" ? " sel" : "")} disabled={!hay}
        aria-pressed={abierto === "validacion"} onClick={() => alternar("validacion")}
        title="Que la celda se elija de una lista (y marcar lo que no está en ella)"
      >▾ Lista desplegable</button>
    </>
  );
}

/* Una muestra del estilo de una regla, para reconocerla de un vistazo. */
function Muestra({ estilo }) {
  return (
    <span
      className="cond-muestra" aria-hidden="true"
      style={{
        background: estilo.bg || "transparent",
        color: estilo.color || (estilo.bg ? "#17212b" : "inherit"),
        fontWeight: estilo.bold ? 700 : 400,
      }}
    >Aa</span>
  );
}

/* El rango del formulario sigue a la selección mientras la persona no lo
   escriba a mano. */
function useRangoDelFormulario(rango) {
  const texto = rango ? textoRango(rango) : "";
  const [valor, setValor] = useState(texto);
  const [tocado, setTocado] = useState(false);
  useEffect(() => { if (!tocado) setValor(texto); }, [texto, tocado]);
  return [valor, (v) => { setValor(v); setTocado(true); }, () => setTocado(false)];
}

function PanelFormato({ rango, reglas, onCambiar }) {
  const [tipo, setTipo] = useState(TIPO_POR_DEFECTO);
  const [campos, setCampos] = useState({ valor: "", valor2: "", texto: "", dias: "15", formula: "" });
  const [estilo, setEstilo] = useState(ESTILO_INICIAL);
  const [error, setError] = useState(null);
  const [rangoTxt, setRangoTxt, soltarRango] = useRangoDelFormulario(rango);
  const def = TIPOS.find((t) => t.tipo === tipo) || TIPOS[0];
  const delRango = reglasEn(reglas, rango);
  const campo = (k) => (e) => setCampos((c) => ({ ...c, [k]: e.target.value }));
  const primera = parseRango(rangoTxt);

  const agregar = (e) => {
    e.preventDefault();
    const { regla, error: falta } = armarRegla({ tipo, rango: rangoTxt, ...campos, estilo });
    if (falta) { setError(falta); return; }
    setError(null);
    soltarRango();
    onCambiar([...reglas, regla]);
  };
  const borrar = (i) => onCambiar(reglas.filter((_, j) => j !== i));
  // Subir una regla le da prioridad: la primera que se cumple es la que pinta.
  const subir = (i) => {
    if (i <= 0) return;
    const n = reglas.slice();
    [n[i - 1], n[i]] = [n[i], n[i - 1]];
    onCambiar(n);
  };

  return (
    <>
      {delRango.length ? (
        <ol className="cond-lista" aria-label="Reglas del rango">
          {delRango.map((i) => {
            const r = reglas[i];
            return (
              <li key={i}>
                <span className="cond-orden" title="Orden: la primera que se cumple pinta la celda">{i + 1}</span>
                <Muestra estilo={r.estilo || {}} />
                <span className="cond-texto">
                  <b>{r.rango}</b>: si {describirRegla(r)}
                </span>
                <button
                  className="esp-btn chico" disabled={i === 0} onClick={() => subir(i)}
                  aria-label={`Darle prioridad a la regla ${i + 1}`} title="Subir: que se mire antes"
                >↑</button>
                <button className="esp-btn chico" onClick={() => borrar(i)} aria-label={`Borrar la regla ${i + 1}`}>Borrar</button>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="tenue cond-vacio">Este rango no tiene reglas todavía.</p>
      )}

      <form className="cond-form" onSubmit={agregar}>
        <label>
          <span>Rango</span>
          <input
            value={rangoTxt} onChange={(e) => setRangoTxt(e.target.value)} aria-label="Rango de la regla"
            placeholder="E2:E100" spellCheck={false} autoComplete="off" className="cond-rango"
          />
        </label>
        <label>
          <span>Si la celda…</span>
          <select value={tipo} onChange={(e) => { setTipo(e.target.value); setError(null); }} aria-label="Condición">
            {TIPOS.map((t) => <option key={t.tipo} value={t.tipo}>{t.etiqueta}</option>)}
          </select>
        </label>
        {def.campos.includes("valor") && (
          <label>
            <span>{tipo === "entre" ? "Desde" : "Número"}</span>
            <input value={campos.valor} onChange={campo("valor")} inputMode="decimal" aria-label="Número" />
          </label>
        )}
        {def.campos.includes("valor2") && (
          <label>
            <span>Hasta</span>
            <input value={campos.valor2} onChange={campo("valor2")} inputMode="decimal" aria-label="Hasta" />
          </label>
        )}
        {def.campos.includes("texto") && (
          <label>
            <span>Texto</span>
            <input value={campos.texto} onChange={campo("texto")} aria-label="Texto" />
          </label>
        )}
        {def.campos.includes("dias") && (
          <label>
            <span>Días</span>
            <input value={campos.dias} onChange={campo("dias")} inputMode="numeric" aria-label="Días" className="cond-corto" />
          </label>
        )}
        {def.campos.includes("formula") && (
          <label className="cond-ancho">
            <span>Fórmula</span>
            <input
              value={campos.formula} onChange={campo("formula")} aria-label="Fórmula"
              placeholder={primera ? `=$E${primera.r0 + 1}<15` : "=$E2<15"} spellCheck={false} autoComplete="off"
            />
          </label>
        )}

        <div className="cond-estilo" role="group" aria-label="Cómo se pinta">
          <span className="tenue">Fondo</span>
          {FONDOS_REGLA.map((c) => (
            <button
              type="button" key={c || "none"} aria-label={"fondo de la regla " + (c || "ninguno")}
              className={"esp-swatch" + ((estilo.bg || "") === c ? " sel" : "")}
              style={{ background: c || "transparent" }}
              onClick={() => setEstilo((s) => ({ ...s, bg: c || undefined }))}
            >{c ? "" : "∅"}</button>
          ))}
          <span className="tenue">Texto</span>
          {TEXTOS_REGLA.map((c) => (
            <button
              type="button" key={c || "none"} aria-label={"texto de la regla " + (c || "por defecto")}
              className={"esp-swatch letra" + ((estilo.color || "") === c ? " sel" : "")}
              style={{ color: c || "inherit" }}
              onClick={() => setEstilo((s) => ({ ...s, color: c || undefined }))}
            >A</button>
          ))}
          <button
            type="button" className={"esp-btn chico" + (estilo.bold ? " sel" : "")} aria-pressed={!!estilo.bold}
            onClick={() => setEstilo((s) => ({ ...s, bold: s.bold ? undefined : true }))} aria-label="Negrita en la regla"
          ><b>B</b></button>
          <Muestra estilo={estilo} />
        </div>

        <button type="submit" className="esp-btn">Agregar regla</button>
        {error && <span className="cond-error" role="alert">{error}</span>}
      </form>

      <p className="tenue cond-ayuda">
        Miran el valor que se VE en la celda (el resultado de la fórmula). Las reglas se miran en
        orden y <b>la primera que se cumple pinta la celda</b>; con ↑ le das prioridad. Las fechas
        van como DD/MM/AAAA. La fórmula personalizada se escribe para la primera celda del rango
        (arriba a la izquierda) y en las demás se corre sola, como al copiar: <code>=$E2&lt;15</code>.
        No viaja al bajar a Excel.
      </p>
    </>
  );
}

function PanelLista({ rango, reglas, onCambiar }) {
  const [modo, setModo] = useState("lista");
  const [opciones, setOpciones] = useState("");
  const [fuente, setFuente] = useState("");
  const [error, setError] = useState(null);
  const [rangoTxt, setRangoTxt, soltarRango] = useRangoDelFormulario(rango);
  const delRango = reglasEn(reglas, rango);

  const agregar = (e) => {
    e.preventDefault();
    const { regla, error: falta } = armarValidacion({ rango: rangoTxt, modo, opciones, fuente });
    if (falta) { setError(falta); return; }
    setError(null);
    soltarRango();
    // La nueva va primero (manda sobre las que ya había en esas celdas), y se
    // van las viejas que quedan enteras adentro de su rango: es reemplazarlas.
    const nuevo = parseRango(regla.rango);
    const quedan = reglas.filter((x) => {
      const r = parseRango(x.rango);
      return !(r && r.r0 >= nuevo.r0 && r.r1 <= nuevo.r1 && r.c0 >= nuevo.c0 && r.c1 <= nuevo.c1);
    });
    onCambiar([regla, ...quedan]);
  };

  return (
    <>
      {delRango.length ? (
        <ul className="cond-lista" aria-label="Listas del rango">
          {delRango.map((i) => {
            const r = reglas[i];
            return (
              <li key={i}>
                <span className="cond-texto">
                  <b>{r.rango}</b>: {r.fuente ? <>las opciones de <code>{r.fuente}</code></> : r.lista.join(" · ")}
                </span>
                <button className="esp-btn chico" onClick={() => onCambiar(reglas.filter((_, j) => j !== i))} aria-label={`Sacar la lista de ${r.rango}`}>Sacar</button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="tenue cond-vacio">Este rango no tiene lista desplegable.</p>
      )}

      <form className="cond-form" onSubmit={agregar}>
        <label>
          <span>Rango</span>
          <input
            value={rangoTxt} onChange={(e) => setRangoTxt(e.target.value)} aria-label="Rango de la lista"
            placeholder="C2:C50" spellCheck={false} autoComplete="off" className="cond-rango"
          />
        </label>
        <div className="cond-modo" role="radiogroup" aria-label="De dónde salen las opciones">
          <label><input type="radio" checked={modo === "lista"} onChange={() => setModo("lista")} /> Escribirlas</label>
          <label><input type="radio" checked={modo === "rango"} onChange={() => setModo("rango")} /> Tomarlas de un rango</label>
        </div>
        {modo === "lista" ? (
          <label className="cond-ancho">
            <span>Opciones</span>
            <input
              value={opciones} onChange={(e) => setOpciones(e.target.value)} aria-label="Opciones"
              placeholder="En ruta, En base, Taller"
            />
          </label>
        ) : (
          <label className="cond-ancho">
            <span>Rango de opciones</span>
            <input
              value={fuente} onChange={(e) => setFuente(e.target.value)} aria-label="Rango de opciones"
              placeholder="=Listas!A1:A5" spellCheck={false} autoComplete="off"
            />
          </label>
        )}
        <button type="submit" className="esp-btn">Agregar lista</button>
        {error && <span className="cond-error" role="alert">{error}</span>}
      </form>
      <p className="tenue cond-ayuda">
        La celda muestra una flechita ▾: tocala para elegir (o <kbd>Alt</kbd>+<kbd>↓</kbd>). Se
        puede seguir escribiendo a mano: lo que no esté en la lista queda marcado, no se bloquea.
        Opciones separadas por coma, punto y coma o renglón.
      </p>
    </>
  );
}

/* El panel de abajo de la barra. `cual` es "condicional" o "validacion";
   `onCambiar(campo, lista)` guarda la nueva lista de reglas de la hoja. */
export function PanelReglas({ cual, rango, hoja, onCambiar, onCerrar }) {
  const esFormato = cual === "condicional";
  const reglas = (hoja && hoja[cual]) || [];
  return (
    <section className="cond-panel" aria-label={esFormato ? "Formato condicional" : "Lista desplegable"}>
      <header>
        <h4>
          {esFormato ? "Formato condicional" : "Lista desplegable"}
          {rango && <span className="tenue"> · {textoRango(rango)}</span>}
        </h4>
        <button className="esp-btn chico" onClick={onCerrar} aria-label="Cerrar el panel">×</button>
      </header>
      {esFormato
        ? <PanelFormato rango={rango} reglas={reglas} onCambiar={(n) => onCambiar(cual, n)} />
        : <PanelLista rango={rango} reglas={reglas} onCambiar={(n) => onCambiar(cual, n)} />}
    </section>
  );
}

/* La lista que se abre al tocar la flechita. Se ubica abajo de la celda (o
   arriba, si no entra) y se cierra tocando afuera, con Escape o al
   desplazar la grilla. */
export function MenuLista({ opciones, actual, x, y, ancho, onElegir, onCerrar }) {
  const ref = useRef(null);
  // En un ref: la pantalla le pasa una función nueva en cada render y el
  // efecto no tiene que volver a correr (ni a mover el foco) por eso.
  const cerrar = useRef(onCerrar);
  cerrar.current = onCerrar;
  useEffect(() => {
    const afuera = (e) => { if (ref.current && !ref.current.contains(e.target)) cerrar.current(); };
    const tecla = (e) => { if (e.key === "Escape") cerrar.current(); };
    const desplazo = (e) => { if (ref.current && !ref.current.contains(e.target)) cerrar.current(); };
    window.addEventListener("pointerdown", afuera);
    window.addEventListener("mousedown", afuera);
    window.addEventListener("keydown", tecla);
    window.addEventListener("scroll", desplazo, true);
    // El foco va a la opción elegida (o la primera): flechas y Enter andan.
    const el = ref.current && (ref.current.querySelector("[aria-selected='true']") || ref.current.querySelector("button"));
    if (el) el.focus({ preventScroll: true });
    return () => {
      window.removeEventListener("pointerdown", afuera);
      window.removeEventListener("mousedown", afuera);
      window.removeEventListener("keydown", tecla);
      window.removeEventListener("scroll", desplazo, true);
    };
  }, []);

  const alto = Math.min(320, 44 * (opciones.length + 1) + 12);
  const abajo = typeof window === "undefined" || y + alto < window.innerHeight;
  const actualTxt = String(actual ?? "").trim().toLowerCase();
  const moverFoco = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const botones = [...ref.current.querySelectorAll("button")];
    const i = botones.indexOf(document.activeElement);
    const j = Math.max(0, Math.min(botones.length - 1, i + (e.key === "ArrowDown" ? 1 : -1)));
    botones[j].focus();
  };
  return (
    <div
      ref={ref} className="cond-menu" role="listbox" aria-label="Opciones de la lista"
      style={{
        left: Math.max(4, Math.min(x, (typeof window !== "undefined" ? window.innerWidth : 1000) - 230)),
        top: abajo ? y : Math.max(4, y - alto),
        minWidth: Math.max(160, ancho || 0),
      }}
      onKeyDown={moverFoco}
    >
      {opciones.length === 0 && <p className="tenue">La lista no tiene opciones.</p>}
      {opciones.map((o) => (
        <button
          key={o} type="button" role="option" aria-selected={o.toLowerCase() === actualTxt}
          onClick={() => onElegir(o)}
        >{o}</button>
      ))}
      {actualTxt && (
        <button type="button" role="option" aria-selected={false} className="cond-menu-vaciar" onClick={() => onElegir("")}>
          Vaciar la celda
        </button>
      )}
    </div>
  );
}

export default PanelReglas;
